import React, { useEffect, useState, useCallback } from 'react';
import { adminCarSpecificationsService } from '../../services/adminService';

const CarSpecificationsForm = ({ carId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [specs, setSpecs] = useState({
    // Basic specifications
    mileage: '',
    engine_displacement: '',
    ground_clearance: '',
    boot_space: '',
    fuel_tank_capacity: '',
    power: '',
    torque: '',
    
    // Dimensions & Capacity
    wheelbase: '',
    length: '',
    width: '',
    height: '',
    alloy_wheels: '',
    front_tyre_size: '',
    rear_tyre_size: '',
    number_of_doors: '',
    
    // Engine & Transmission
    drive_train: '',
    gear_box: '',
    number_of_gears: '',
    number_of_cylinders: '',
    valve_configuration: '',
    turbocharger: '',
    lsd: '',
    
    // Fuel & Performance
    mileage_city: '',
    mileage_highway: '',
    emission_class: '',
    
    // Suspension, Steering & Brakes
    steering_type: '',
    turning_radius: '',
    front_brake_type: '',
    rear_brake_type: '',
    front_suspension: '',
    rear_suspension: ''
  });

  const loadSpecifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminCarSpecificationsService.getSpecification(carId);
      if (res.specification) {
        setSpecs(res.specification);
      }
    } catch (error) {
      console.log('No specifications found yet');
    } finally {
      setLoading(false);
    }
  }, [carId]);

  useEffect(() => {
    if (carId) {
      loadSpecifications();
    }
  }, [carId, loadSpecifications]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await adminCarSpecificationsService.upsertSpecification(carId, specs);
      alert('Specifications saved successfully!');
    } catch (error) {
      alert('Failed to save specifications: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSpecs(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="car-specifications-form">
        <h3>⚙️ Car Specifications</h3>
        <div className="loading-state">Loading specifications...</div>
      </div>
    );
  }

  return (
    <div className="car-specifications-form">
      <div className="form-header">
        <h3>⚙️ Car Specifications</h3>
      </div>

      <form onSubmit={handleSubmit} className="spec-form">
        {/* Basic Specifications */}
        <div className="spec-section">
          <h4>Basic Specifications</h4>
          <div className="spec-grid">
            <div className="form-group">
              <label>Mileage (km/l)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 18.5"
                value={specs.mileage}
                onChange={(e) => handleChange('mileage', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Engine Displacement (cc)</label>
              <input
                type="number"
                placeholder="e.g., 1500"
                value={specs.engine_displacement}
                onChange={(e) => handleChange('engine_displacement', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Power</label>
              <input
                type="text"
                placeholder="e.g., 110 bhp @ 6000 rpm"
                value={specs.power}
                onChange={(e) => handleChange('power', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Torque</label>
              <input
                type="text"
                placeholder="e.g., 200 Nm @ 2000 rpm"
                value={specs.torque}
                onChange={(e) => handleChange('torque', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Ground Clearance (mm)</label>
              <input
                type="number"
                placeholder="e.g., 170"
                value={specs.ground_clearance}
                onChange={(e) => handleChange('ground_clearance', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Boot Space (L)</label>
              <input
                type="number"
                placeholder="e.g., 400"
                value={specs.boot_space}
                onChange={(e) => handleChange('boot_space', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Fuel Tank Capacity (L)</label>
              <input
                type="number"
                placeholder="e.g., 50"
                value={specs.fuel_tank_capacity}
                onChange={(e) => handleChange('fuel_tank_capacity', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Dimensions & Capacity */}
        <div className="spec-section">
          <h4>Dimensions & Capacity</h4>
          <div className="spec-grid">
            <div className="form-group">
              <label>Wheelbase (mm)</label>
              <input
                type="number"
                placeholder="e.g., 2600"
                value={specs.wheelbase}
                onChange={(e) => handleChange('wheelbase', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Length (mm)</label>
              <input
                type="number"
                placeholder="e.g., 4300"
                value={specs.length}
                onChange={(e) => handleChange('length', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Width (mm)</label>
              <input
                type="number"
                placeholder="e.g., 1800"
                value={specs.width}
                onChange={(e) => handleChange('width', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Height (mm)</label>
              <input
                type="number"
                placeholder="e.g., 1600"
                value={specs.height}
                onChange={(e) => handleChange('height', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Alloy Wheels</label>
              <input
                type="text"
                placeholder="e.g., 16-inch"
                value={specs.alloy_wheels}
                onChange={(e) => handleChange('alloy_wheels', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Front Tyre Size</label>
              <input
                type="text"
                placeholder="e.g., 195/65 R15"
                value={specs.front_tyre_size}
                onChange={(e) => handleChange('front_tyre_size', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Rear Tyre Size</label>
              <input
                type="text"
                placeholder="e.g., 195/65 R15"
                value={specs.rear_tyre_size}
                onChange={(e) => handleChange('rear_tyre_size', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Number of Doors</label>
              <input
                type="number"
                placeholder="e.g., 4"
                value={specs.number_of_doors}
                onChange={(e) => handleChange('number_of_doors', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Engine & Transmission */}
        <div className="spec-section">
          <h4>Engine & Transmission</h4>
          <div className="spec-grid">
            <div className="form-group">
              <label>Transmission</label>
              <select
                value={specs.gear_box}
                onChange={(e) => handleChange('gear_box', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Manual">Manual</option>
                <option value="Automatic">Automatic</option>
                <option value="AMT">AMT</option>
                <option value="CVT">CVT</option>
                <option value="DCT">DCT</option>
              </select>
            </div>

            <div className="form-group">
              <label>Number of Gears</label>
              <input
                type="number"
                placeholder="e.g., 5"
                value={specs.number_of_gears}
                onChange={(e) => handleChange('number_of_gears', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Drive Train</label>
              <select
                value={specs.drive_train}
                onChange={(e) => handleChange('drive_train', e.target.value)}
              >
                <option value="">Select</option>
                <option value="FWD">Front Wheel Drive (FWD)</option>
                <option value="RWD">Rear Wheel Drive (RWD)</option>
                <option value="AWD">All Wheel Drive (AWD)</option>
                <option value="4WD">Four Wheel Drive (4WD)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Number of Cylinders</label>
              <input
                type="number"
                placeholder="e.g., 4"
                value={specs.number_of_cylinders}
                onChange={(e) => handleChange('number_of_cylinders', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Valve Configuration</label>
              <input
                type="text"
                placeholder="e.g., DOHC"
                value={specs.valve_configuration}
                onChange={(e) => handleChange('valve_configuration', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Turbocharger</label>
              <select
                value={specs.turbocharger}
                onChange={(e) => handleChange('turbocharger', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="form-group">
              <label>LSD (Limited Slip Differential)</label>
              <select
                value={specs.lsd}
                onChange={(e) => handleChange('lsd', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
        </div>

        {/* Fuel & Performance */}
        <div className="spec-section">
          <h4>Fuel & Performance</h4>
          <div className="spec-grid">
            <div className="form-group">
              <label>City Mileage (km/l)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 16.5"
                value={specs.mileage_city}
                onChange={(e) => handleChange('mileage_city', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Highway Mileage (km/l)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 20.5"
                value={specs.mileage_highway}
                onChange={(e) => handleChange('mileage_highway', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Emission Class</label>
              <select
                value={specs.emission_class}
                onChange={(e) => handleChange('emission_class', e.target.value)}
              >
                <option value="">Select</option>
                <option value="BS6">BS6</option>
                <option value="BS4">BS4</option>
                <option value="Euro 6">Euro 6</option>
              </select>
            </div>
          </div>
        </div>

        {/* Suspension, Steering & Brakes */}
        <div className="spec-section">
          <h4>Suspension, Steering & Brakes</h4>
          <div className="spec-grid">
            <div className="form-group">
              <label>Steering Type</label>
              <select
                value={specs.steering_type}
                onChange={(e) => handleChange('steering_type', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Power">Power Steering</option>
                <option value="Electric">Electric Power Steering</option>
                <option value="Manual">Manual</option>
              </select>
            </div>

            <div className="form-group">
              <label>Turning Radius (m)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 5.2"
                value={specs.turning_radius}
                onChange={(e) => handleChange('turning_radius', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Front Brake Type</label>
              <select
                value={specs.front_brake_type}
                onChange={(e) => handleChange('front_brake_type', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Disc">Disc</option>
                <option value="Drum">Drum</option>
                <option value="Ventilated Disc">Ventilated Disc</option>
              </select>
            </div>

            <div className="form-group">
              <label>Rear Brake Type</label>
              <select
                value={specs.rear_brake_type}
                onChange={(e) => handleChange('rear_brake_type', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Disc">Disc</option>
                <option value="Drum">Drum</option>
              </select>
            </div>

            <div className="form-group">
              <label>Front Suspension</label>
              <input
                type="text"
                placeholder="e.g., MacPherson Strut"
                value={specs.front_suspension}
                onChange={(e) => handleChange('front_suspension', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Rear Suspension</label>
              <input
                type="text"
                placeholder="e.g., Torsion Beam"
                value={specs.rear_suspension}
                onChange={(e) => handleChange('rear_suspension', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? '💾 Saving...' : '💾 Save Specifications'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CarSpecificationsForm;